<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getUnixTime } from 'date-fns';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { vOnClickOutside } from '@vueuse/components';
import { formatMoney } from './utils/currency';
import { rottingDays } from './utils/rotting';

// Card "rico" do board (estilo Pipedrive/HubSpot): linha de título com ícone
// de canal + identificação da conversa e indicador de prioridade, e uma linha
// de meta com responsável e última atividade. As etiquetas ficam secundárias.
// Reaproveita componentes do design-system do Chatwoot (ChannelIcon, Avatar,
// CardPriorityIcon) para herdar o visual/ícones nativos por tipo de canal.
const props = defineProps({
  // Card serializado pelo board single-stage. Campos usados:
  // id, display_id, contact_name, priority, last_activity_at, channel_type,
  // value (float), assignee ({ id, name } | null), label_list (string[]).
  card: {
    type: Object,
    required: true,
  },
  // Estágio dono do card; usado para o cálculo de "rotting" (rotting_days).
  stage: {
    type: Object,
    default: () => ({}),
  },
  // Estágios-alvo para as ações rápidas "Marcar Ganho/Perdido". Cada um é o
  // primeiro estágio com behavior 'won'/'lost' do pipeline (ou null).
  wonStage: {
    type: Object,
    default: null,
  },
  lostStage: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['open', 'setValue', 'markStage', 'setPriority']);

const { t } = useI18n();

// ChannelIcon espera um "inbox" com channel_type; montamos o objeto mínimo a
// partir do card. Só renderizamos o ícone quando o tipo de canal está presente.
const channelInbox = computed(() =>
  props.card.channel_type ? { channel_type: props.card.channel_type } : null
);

const title = computed(() =>
  `#${props.card.display_id} ${props.card.contact_name || ''}`.trim()
);

const assignee = computed(() => props.card.assignee || null);

// Indicador de prioridade discreto: só para medium/high/urgent (low e vazio
// não poluem o card).
const SHOWN_PRIORITIES = [
  CONVERSATION_PRIORITY.MEDIUM,
  CONVERSATION_PRIORITY.HIGH,
  CONVERSATION_PRIORITY.URGENT,
];
const showPriority = computed(() =>
  SHOWN_PRIORITIES.includes(props.card.priority)
);

// "há X" a partir do ISO `last_activity_at`, no formato compacto usado nos
// cards de conversa do Chatwoot (ex.: "3h", "2d"). dynamicTime trabalha com
// timestamp Unix, então convertemos o ISO antes.
const lastActivityLabel = computed(() => {
  if (!props.card.last_activity_at) return '';
  const unix = getUnixTime(new Date(props.card.last_activity_at));
  if (Number.isNaN(unix)) return '';
  return shortTimestamp(dynamicTime(unix), true);
});

const labels = computed(() => props.card.label_list || []);

// --- Valor do negócio -----------------------------------------------------
const numericValue = computed(() => Number(props.card.value) || 0);
const hasValue = computed(() => numericValue.value > 0);
const formattedValue = computed(() => formatMoney(numericValue.value));

const isEditingValue = ref(false);
const valueDraft = ref('');
const valueInput = ref(null);

const startEditValue = async () => {
  valueDraft.value = hasValue.value ? String(numericValue.value) : '';
  isEditingValue.value = true;
  await nextTick();
  valueInput.value?.focus();
  valueInput.value?.select();
};

const cancelEditValue = () => {
  isEditingValue.value = false;
};

const commitValue = () => {
  if (!isEditingValue.value) return;
  isEditingValue.value = false;
  const parsed = Number(valueDraft.value);
  const next = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  if (next === numericValue.value) return;
  emit('setValue', { card: props.card, value: next });
};

// --- Rotting --------------------------------------------------------------
const rottenDays = computed(() =>
  rottingDays({
    rottingDays: props.stage?.rotting_days,
    lastActivityAt: props.card.last_activity_at,
  })
);
const isRotting = computed(() => rottenDays.value > 0);

// --- Ações rápidas (menu ⋯) -----------------------------------------------
const showActions = ref(false);

const stageBehavior = computed(() => props.stage?.behavior || 'open');

const quickActions = computed(() => {
  const items = [];
  // Só oferece "Ganho" quando há estágio won e o card não está já lá.
  if (props.wonStage && stageBehavior.value !== 'won') {
    items.push({
      action: 'won',
      label: t('PIPELINES.BOARD.CARD.ACTIONS.MARK_WON'),
      icon: 'i-lucide-trophy',
    });
  }
  if (props.lostStage && stageBehavior.value !== 'lost') {
    items.push({
      action: 'lost',
      label: t('PIPELINES.BOARD.CARD.ACTIONS.MARK_LOST'),
      icon: 'i-lucide-x-circle',
    });
  }
  // Chaves de i18n estáticas (sem template-literal) para não disparar o lint
  // de dynamic-keys do vue-i18n.
  const priorityItems = [
    {
      value: CONVERSATION_PRIORITY.URGENT,
      label: t('PIPELINES.BOARD.CARD.ACTIONS.PRIORITY_URGENT'),
    },
    {
      value: CONVERSATION_PRIORITY.HIGH,
      label: t('PIPELINES.BOARD.CARD.ACTIONS.PRIORITY_HIGH'),
    },
    {
      value: CONVERSATION_PRIORITY.MEDIUM,
      label: t('PIPELINES.BOARD.CARD.ACTIONS.PRIORITY_MEDIUM'),
    },
    {
      value: CONVERSATION_PRIORITY.LOW,
      label: t('PIPELINES.BOARD.CARD.ACTIONS.PRIORITY_LOW'),
    },
  ];
  priorityItems.forEach(({ value, label }) => {
    items.push({
      action: 'priority',
      value,
      label,
      icon: 'i-lucide-signal',
      isSelected: props.card.priority === value,
    });
  });
  return items;
});

const onAction = ({ action, value }) => {
  showActions.value = false;
  if (action === 'won' && props.wonStage) {
    emit('markStage', { card: props.card, toStage: props.wonStage });
  } else if (action === 'lost' && props.lostStage) {
    emit('markStage', { card: props.card, toStage: props.lostStage });
  } else if (action === 'priority') {
    emit('setPriority', { card: props.card, priority: value });
  }
};
</script>

<template>
  <div
    role="button"
    tabindex="0"
    :title="t('PIPELINES.BOARD.OPEN_DIALOG')"
    class="relative flex flex-col gap-2 p-3 rounded-lg transition-colors cursor-pointer outline outline-1 outline-n-weak bg-n-background hover:outline-n-brand hover:bg-n-alpha-1 focus:outline-n-brand focus:outline-2"
    @click="$emit('open', card)"
    @keydown.enter="$emit('open', card)"
  >
    <!-- Linha de título: canal + #id contato + prioridade + ações -->
    <div class="flex items-start gap-2">
      <ChannelIcon
        v-if="channelInbox"
        :inbox="channelInbox"
        use-brand-icon
        class="flex-shrink-0 mt-px size-4"
      />
      <span
        class="flex-grow min-w-0 text-sm font-medium truncate text-n-slate-12"
      >
        {{ title }}
      </span>
      <CardPriorityIcon
        v-if="showPriority"
        :priority="card.priority"
        class="flex-shrink-0 !text-n-slate-11"
      />
      <!-- Menu de ações rápidas (não abre a conversa) -->
      <div
        v-if="quickActions.length"
        v-on-click-outside="() => (showActions = false)"
        class="relative flex-shrink-0"
      >
        <button
          type="button"
          class="grid -mt-0.5 -mr-1 rounded place-items-center size-5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
          :title="t('PIPELINES.BOARD.CARD.ACTIONS.MENU')"
          :aria-label="t('PIPELINES.BOARD.CARD.ACTIONS.MENU')"
          @click.stop="showActions = !showActions"
        >
          <span class="size-4 i-lucide-ellipsis-vertical" />
        </button>
        <DropdownMenu
          v-if="showActions"
          :menu-items="quickActions"
          class="ltr:right-0 rtl:left-0 top-6"
          @click.stop
          @action="onAction"
        />
      </div>
    </div>

    <!-- Valor do negócio + badge de "parado" (rotting) -->
    <div
      v-if="hasValue || isEditingValue || isRotting"
      class="flex flex-wrap items-center gap-2"
    >
      <input
        v-if="isEditingValue"
        ref="valueInput"
        v-model="valueDraft"
        type="number"
        min="0"
        step="0.01"
        class="w-24 px-2 py-1 text-sm rounded-md outline outline-1 outline-n-brand bg-n-background text-n-slate-12"
        :aria-label="t('PIPELINES.BOARD.CARD.VALUE_LABEL')"
        @click.stop
        @keydown.stop
        @keydown.enter.prevent="commitValue"
        @keydown.esc.prevent="cancelEditValue"
        @blur="commitValue"
      />
      <button
        v-else-if="hasValue"
        type="button"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md text-n-teal-11 bg-n-teal-3 hover:bg-n-teal-4"
        :title="t('PIPELINES.BOARD.CARD.VALUE_EDIT')"
        @click.stop="startEditValue"
      >
        <span class="size-3 i-lucide-circle-dollar-sign" />
        {{ formattedValue }}
      </button>
      <span
        v-if="isRotting"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md text-n-amber-11 bg-n-amber-3"
        :title="t('PIPELINES.BOARD.CARD.ROTTING_TITLE')"
      >
        <span class="size-3 i-lucide-flame" />
        {{ t('PIPELINES.BOARD.CARD.ROTTING', { days: rottenDays }) }}
      </span>
    </div>
    <!-- Atalho discreto para definir valor quando ainda não há -->
    <button
      v-else
      type="button"
      class="self-start inline-flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-brand"
      @click.stop="startEditValue"
    >
      <span class="size-3 i-lucide-circle-dollar-sign" />
      {{ t('PIPELINES.BOARD.CARD.ADD_VALUE') }}
    </button>

    <!-- Linha de meta: responsável + última atividade -->
    <div class="flex items-center gap-2 text-xs text-n-slate-11">
      <span class="flex items-center flex-grow min-w-0 gap-1.5">
        <template v-if="assignee">
          <Avatar :name="assignee.name" :size="18" rounded-full />
          <span class="truncate text-n-slate-11">{{ assignee.name }}</span>
        </template>
        <template v-else>
          <span
            class="flex-shrink-0 size-4 i-lucide-user-round text-n-slate-9"
          />
          <span class="truncate text-n-slate-10">
            {{ t('PIPELINES.BOARD.CARD.UNASSIGNED') }}
          </span>
        </template>
      </span>
      <span
        v-if="lastActivityLabel"
        class="flex items-center flex-shrink-0 gap-1 text-n-slate-10"
        :title="t('PIPELINES.BOARD.CARD.LAST_ACTIVITY')"
      >
        <span class="size-3 i-lucide-clock" />
        {{ lastActivityLabel }}
      </span>
    </div>

    <!-- Etiquetas (secundárias, menores) -->
    <div v-if="labels.length" class="flex flex-wrap gap-1">
      <span
        v-for="lbl in labels"
        :key="lbl"
        class="px-1 py-0.5 text-[10px] rounded text-n-slate-11 bg-n-alpha-2"
      >
        {{ lbl }}
      </span>
    </div>
  </div>
</template>
